import type { Appearance, StripeElementsOptions } from "@stripe/stripe-js";
import { alpha, useTheme } from "@mui/material/styles";
import { usePlatform } from "@/shared/hooks";
import {
  buildGoogleFontsUrl,
  DEFAULT_FONTS,
  parseFontFamily,
} from "@/shared/utils/fonts";

/**
 * Опции Stripe Elements из темы витрины, как в референсном
 * StripePaymentForm.js: цвета из палитры, радиус из shape, шрифт текста
 * платформы (или дефолтный Manrope) подгружается в iframe Stripe через
 * Google Fonts, потому что self-hosted стеки next/font туда не доезжают.
 * Общие для чекаута и диалога замены карты подписки.
 */
export function useStripeElementsOptions(
  clientSecret: string,
): StripeElementsOptions {
  const theme = useTheme();
  const { platform } = usePlatform();

  const fontFamily =
    parseFontFamily(platform?.theme?.typography?.fontFamily)?.family ??
    DEFAULT_FONTS.base;
  const fontCssSrc = buildGoogleFontsUrl([fontFamily]);
  const dark = theme.palette.mode === "dark";

  const appearance: Appearance = {
    theme: dark ? "night" : "stripe",
    variables: {
      colorPrimary: theme.palette.primary.main,
      colorBackground: theme.palette.background.paper,
      colorText: theme.palette.text.primaryInHex,
      colorTextSecondary: theme.palette.text.secondary,
      colorTextPlaceholder: theme.palette.text.muted,
      colorDanger: theme.palette.error.main,
      fontFamily: `"${fontFamily}", sans-serif`,
      fontSizeBase: "15px",
      borderRadius: `${theme.shape.borderRadius}px`,
      spacingUnit: "4px",
    },
    rules: {
      ".Input": {
        backgroundColor: theme.palette.background.elevated,
        borderColor: theme.palette.divider,
        boxShadow: "none",
      },
      ".Input:focus": {
        borderColor: theme.palette.primary.main,
        boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
      },
      ".Label": {
        color: theme.palette.text.secondary,
        fontWeight: "600",
      },
      ".Tab, .Block, .PickerItem": {
        backgroundColor: theme.palette.background.elevated,
        borderColor: theme.palette.divider,
        boxShadow: "none",
      },
      ".Tab:hover": {
        borderColor: alpha(theme.palette.text.primary, 0.3),
      },
      ".Tab--selected, .Tab--selected:hover": {
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.light,
      },
    },
  };

  return {
    clientSecret,
    appearance,
    // На проде onReady срабатывал раньше, чем iframe элемента отрисовывал
    // поля: скелетон Stripe занимает высоту формы сразу, до отрисовки полей.
    loader: "always",
    ...(fontCssSrc ? { fonts: [{ cssSrc: fontCssSrc }] } : {}),
  };
}
